import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import BiometrieInvitation from '../components/common/BiometrieInvitation';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useMediaQuery,
  useTheme,
  Chip,
  CircularProgress,
  Tooltip,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Person,
  AttachMoney,
  EventNote,
  LocalAtm,
  VolunteerActivism,
  Description,
  ExitToApp,
  Notifications,
  NotificationsNone,
  Settings,
  Payment,
  Article,
  DoneAll,
  Sell,
  Info as InfoIcon,
  Warning as WarnIcon,
  CheckCircle as OkIcon,
  Error as ErrIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api.service';

// ── Icône par type de notification ──────────────────────────────────────────
const NOTIF_EMOJI = {
  anniversaire:     '🎂',
  assemblee:        '📅',
  assemblee_proche: '📅',
  cotisation:       '💰',
  aide:             '🤝',
  tontine:          '🏆',
  general:          '📢',
};

const NotifIcon = ({ type, priorite }) => {
  const sx = { fontSize: 18, mr: 1, flexShrink: 0 };
  if (NOTIF_EMOJI[type]) {
    return <span style={{ fontSize: 18, marginRight: 8, flexShrink: 0 }}>{NOTIF_EMOJI[type]}</span>;
  }
  if (priorite === 'haute') return <ErrIcon sx={{ ...sx, color: '#c62828' }} />;
  if (type === 'assemblee_proche') return <WarnIcon sx={{ ...sx, color: '#e65100' }} />;
  if (type === 'aide') return <OkIcon sx={{ ...sx, color: '#2e7d32' }} />;
  return <InfoIcon sx={{ ...sx, color: '#1565c0' }} />;
};

// ── Formatage date courte ────────────────────────────────────────────────────
const dateRelative = (d) => {
  if (!d) return '';
  const diff = Math.floor((Date.now() - new Date(d)) / 60000);
  if (diff < 1)  return 'À l\'instant';
  if (diff < 60) return `il y a ${diff} min`;
  const h = Math.floor(diff / 60);
  if (h < 24)    return `il y a ${h}h`;
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// ── Libellé affiché pour le rôle de l'utilisateur connecté ──────────────────
// L'espace "Membre" est partagé par tout le monde (un officier consulte aussi
// ses propres cotisations/assemblées ici) : le badge doit refléter le rôle
// réel plutôt qu'un "MEMBRE" fixe qui masque qu'il s'agit par ex. de la
// secrétaire générale.
const ROLE_LABELS = {
  administrateur:             'ADMINISTRATEUR',
  president:                  'PRÉSIDENT',
  tresorier:                  'TRÉSORIER',
  tresorier_adjoint:          'TRÉSORIER ADJOINT',
  secretaire_general:         'SECRÉTAIRE GÉNÉRAL(E)',
  secretaire_general_adjoint: 'SECRÉTAIRE GÉNÉRAL(E) ADJOINT(E)',
  commissaire:                'COMMISSAIRE AUX COMPTES',
  membre:                     'MEMBRE',
};

const drawerWidth = 280;

const MemberLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);

  // ── État notifications ────────────────────────────────────────────────────
  const [notifs, setNotifs]           = useState([]);
  const [nbNonLues, setNbNonLues]     = useState(0);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const intervalRef = useRef(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Sur téléphone, le menu doit se refermer dès qu'on a touché une page
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  // ── Charger le badge (compteur non lues) ─────────────────────────────────
  const chargerBadge = useCallback(async () => {
    try {
      const res = await api.get('/notifications/non-lues/count');
      if (res.data.success) setNbNonLues(res.data.data.count || 0);
    } catch (_) {}
  }, []);

  // ── Charger la liste complète ─────────────────────────────────────────────
  const chargerNotifs = useCallback(async () => {
    setLoadingNotifs(true);
    try {
      const res = await api.get('/notifications?limit=15');
      if (res.data.success) {
        setNotifs(res.data.data.notifications || []);
        setNbNonLues(res.data.data.non_lues || 0);
      }
    } catch (_) {}
    finally { setLoadingNotifs(false); }
  }, []);

  // ── Polling badge toutes les 60s ─────────────────────────────────────────
  useEffect(() => {
    chargerBadge();
    intervalRef.current = setInterval(chargerBadge, 60000);
    return () => clearInterval(intervalRef.current);
  }, [chargerBadge]);

  // ── Marquer une notif comme lue ───────────────────────────────────────────
  const marquerLue = useCallback(async (id) => {
    try {
      await api.post(`/notifications/${id}/lire`);
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, est_lu: true } : n));
      setNbNonLues(prev => Math.max(0, prev - 1));
    } catch (_) {}
  }, []);

  // ── Tout marquer comme lu ─────────────────────────────────────────────────
  const toutMarquerLu = useCallback(async () => {
    try {
      await api.post('/notifications/tout-lire');
      setNotifs(prev => prev.map(n => ({ ...n, est_lu: true })));
      setNbNonLues(0);
    } catch (_) {}
  }, []);

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
    chargerNotifs(); // charger à l'ouverture
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Menu items pour les membres
  const menuItems = [
    {
      title: 'Tableau de bord',
      path: '/member/dashboard',
      icon: <Dashboard />,
    },
    {
      title: 'Mon Profil',
      path: '/member/profile',
      icon: <Person />,
    },
    {
      title: 'Mes Cotisations',
      path: '/member/cotisations',
      icon: <AttachMoney />,
    },
    {
      title: 'Assemblées',
      path: '/member/assemblees',
      icon: <EventNote />,
    },
    // Bilan de l'association : masqué aux membres jusqu'à validation du bureau.
    // Pour l'ouvrir : rétablir cette entrée de menu (icône BarChart, chemin /member/bilan)
    // ET mettre BILAN_OUVERT_MEMBRES=true dans l'environnement du backend.
    {
      title: 'Blog',
      path: '/blog',  // Route publique accessible aux membres
      icon: <Article />,
    },
    {
      title: 'Tontine',
      path: '/member/tontine',
      icon: <LocalAtm />,
    },
    {
      title: 'Mes Aides',
      path: '/member/aide',
      icon: <VolunteerActivism />,
    },
    {
      title: 'Coin Affaires',
      path: '/member/coin-affaires',
      icon: <Sell />,
    },
    {
      title: 'Documents',
      path: '/member/documents',
      icon: <Description />,
    },
    {
      title: 'Paiements',
      path: '/member/paiement',
      icon: <Payment />,
    }
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo et titre */}
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.dark', color: 'white' }}>
        <Typography variant="h5" fontWeight="bold">
          BON'EPEY'A NTOCKO
        </Typography>
        <Chip
          label={ROLE_LABELS[user?.role] || 'MEMBRE'}
          size="small"
          sx={{ mt: 1, bgcolor: 'white', color: 'primary.dark', fontWeight: 'bold' }}
        />
      </Box>
      
      <Divider />

      {/* User info */}
      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar 
            src={user?.photoUrl}
            alt={user?.prenom}
            sx={{ bgcolor: 'primary.main' }}
          >
            {user?.prenom?.[0]}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">
              {user?.prenom} {user?.nom}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.matricule}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Navigation Menu */}
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.path}
            component={RouterLink}
            to={item.path}
            selected={location.pathname === item.path}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white'
                },
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.title} />
          </ListItemButton>
        ))}
      </List>

      <Divider />

      {/* Logout Button */}
      <List sx={{ p: 1 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
            color: 'error.main',
            '&:hover': {
              bgcolor: 'error.lighter'
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <ExitToApp />
          </ListItemIcon>
          <ListItemText primary="Déconnexion" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Espace Membre
          </Typography>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton size="large" color="inherit" onClick={handleNotificationMenuOpen}>
              <Badge badgeContent={nbNonLues > 0 ? nbNonLues : null} color="error" max={99}>
                {nbNonLues > 0 ? <Notifications /> : <NotificationsNone />}
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Profile Menu */}
          <IconButton
            size="large"
            edge="end"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <Avatar 
              src={user?.photoUrl}
              alt={user?.prenom}
              sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
            >
              {user?.prenom?.[0]}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/member/profile'); }}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Mon Profil
        </MenuItem>
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/member/settings'); }}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Paramètres
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          Déconnexion
        </MenuItem>
      </Menu>

      {/* ── Notifications Menu ── */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxHeight: 500, display: 'flex', flexDirection: 'column' } }}
      >
        {/* En-tête */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Notifications sx={{ color: '#1B5E20', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight="bold">Notifications</Typography>
            {nbNonLues > 0 && (
              <Chip label={nbNonLues} size="small" color="error"
                sx={{ height: 18, fontSize: 11, fontWeight: 'bold' }} />
            )}
          </Box>
          {nbNonLues > 0 && (
            <Tooltip title="Tout marquer comme lu">
              <IconButton size="small" onClick={toutMarquerLu} sx={{ color: '#2E7D32' }}>
                <DoneAll fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Liste notifications */}
        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {loadingNotifs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} sx={{ color: '#1B5E20' }} />
            </Box>
          ) : notifs.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <NotificationsNone sx={{ fontSize: 40, color: '#bdbdbd', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Aucune notification</Typography>
            </Box>
          ) : (
            notifs.map((n) => (
              <MenuItem
                key={n.id}
                onClick={() => { marquerLue(n.id); handleNotificationMenuClose(); }}
                sx={{
                  alignItems: 'flex-start',
                  py: 1.5, px: 2,
                  bgcolor: n.est_lu ? 'transparent' : 'rgba(27,94,32,0.06)',
                  borderLeft: n.est_lu ? 'none' : '3px solid #1B5E20',
                  '&:hover': { bgcolor: 'rgba(27,94,32,0.1)' },
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  whiteSpace: 'normal',
                }}
              >
                <NotifIcon type={n.type} priorite={n.priorite} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={n.est_lu ? 400 : 700}
                    sx={{ lineHeight: 1.3, mb: 0.3 }}>
                    {n.titre}
                  </Typography>
                  {n.message && (
                    <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', lineHeight: 1.3, mb: 0.3 }}>
                      {n.message}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: '#9e9e9e', fontSize: '0.68rem' }}>
                    {dateRelative(n.created_at)}
                  </Typography>
                </Box>
                {!n.est_lu && (
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                    bgcolor: '#1B5E20', flexShrink: 0, mt: 0.5, ml: 1 }} />
                )}
              </MenuItem>
            ))
          )}
        </Box>

        {/* Pied — Tout marquer lu */}
        {notifs.length > 0 && nbNonLues > 0 && (
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <Button
              fullWidth size="small"
              startIcon={<DoneAll />}
              onClick={toutMarquerLu}
              sx={{ py: 1, color: '#1B5E20', fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(27,94,32,0.08)' } }}
            >
              Tout marquer comme lu
            </Button>
          </Box>
        )}
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,            // un contenu trop large ne doit plus elargir la page sur telephone
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          bgcolor: 'grey.50',
          minHeight: '100vh'
        }}
      >
        <Outlet />
        <BiometrieInvitation />
      </Box>
    </Box>
  );
};

export default MemberLayout;
